import { useEffect, useState } from "react";
import * as QRCode from "qrcode";

export interface PersonalRechargeRecord {
  id: string;
  reference: string;
  amount: number;
  channel: RechargeChannel;
  paid_at: string;
}

export interface PersonalWallet {
  total_balance: number;
  recharge_balance: number;
  reward_balance: number;
  available_recharge_channels: RechargeChannel[];
  recharges_total: number;
  recharge_page: number;
  recharge_page_count: number;
  recharges: PersonalRechargeRecord[];
}

export interface PersonalRecharge {
  id: string;
  reference: string;
  amount: number;
  channel: RechargeChannel;
  status: "PENDING" | "PAID" | "FAILED" | "CLOSED";
  pay_url: string | null;
}

export type RechargeChannel = "WECHAT" | "ALIPAY";

const RECHARGES_PER_PAGE = 10;

export function paginationPageNumbers(page: number, pageCount: number): number[] {
  const visibleCount = Math.min(5, pageCount);
  const start = Math.min(Math.max(1, page - 2), Math.max(1, pageCount - visibleCount + 1));
  return Array.from({ length: visibleCount }, (_, index) => start + index);
}

export function isRechargeAmountValid(amount: string): boolean {
  if (!/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(amount.trim())) return false;
  const value = Number(amount);
  return Number.isFinite(value) && value >= 1 && value <= 50000;
}

export function rechargeQrAlt(channel: RechargeChannel): string {
  return channel === "WECHAT" ? "微信付款二维码" : "支付宝付款二维码";
}

export function rechargeStatusCopy(status: PersonalRecharge["status"]): string {
  if (status === "PAID") return "充值已到账";
  if (status === "FAILED") return "充值失败，请重新生成付款二维码";
  if (status === "CLOSED") return "充值订单已关闭，请重新生成";
  return "等待付款，支付完成后余额会自动刷新";
}

const money = (value: number) => `¥${value.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function BalanceAccount({ wallet, onRecharge, onCheckRecharge, onBalanceUpdated, onRechargePageChange }: {
  wallet: PersonalWallet;
  onRecharge: (amount: number, channel: RechargeChannel) => Promise<PersonalRecharge>;
  onCheckRecharge?: (rechargeId: string) => Promise<PersonalRecharge>;
  onBalanceUpdated?: () => Promise<void>;
  onRechargePageChange?: (page: number) => Promise<void>;
}) {
  const availableChannels = wallet.available_recharge_channels ?? [];
  const [amount, setAmount] = useState("500");
  const [channel, setChannel] = useState<RechargeChannel | null>(null);
  const [recharge, setRecharge] = useState<PersonalRecharge | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");

  useEffect(() => {
    let active = true;
    setQrDataUrl("");
    if (!recharge?.pay_url) return () => { active = false; };
    void QRCode.toDataURL(recharge.pay_url, {
      errorCorrectionLevel: "M",
      margin: 2,
      width: 220,
      color: { dark: "#102a4c", light: "#ffffff" },
    }).then((dataUrl) => {
      if (active) setQrDataUrl(dataUrl);
    }).catch(() => {
      if (active) setError("付款二维码生成失败，请重新生成。");
    });
    return () => { active = false; };
  }, [recharge?.pay_url]);

  useEffect(() => {
    if (recharge?.status !== "PENDING" || !onCheckRecharge) return;
    let active = true;
    const timer = window.setInterval(() => {
      void onCheckRecharge(recharge.id).then(async (latest) => {
        if (!active) return;
        if (latest.status === "PAID") {
          window.clearInterval(timer);
          setNotice("充值已到账，余额和流水已更新。");
          setRecharge(latest);
          await onBalanceUpdated?.();
        } else if (latest.status === "FAILED" || latest.status === "CLOSED") {
          window.clearInterval(timer);
          setNotice(rechargeStatusCopy(latest.status));
          setRecharge(latest);
        }
      }).catch(() => {
        // 短暂网络失败时保留当前付款页，下一轮继续查询。
      });
    }, 2500);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [onBalanceUpdated, onCheckRecharge, recharge?.id, recharge?.status]);

  async function submit() {
    const value = Number(amount);
    if (!isRechargeAmountValid(amount) || !channel || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      setRecharge(await onRecharge(value, channel));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "充值订单创建失败");
    } finally {
      setBusy(false);
    }
  }

  function resetPayment() {
    setRecharge(null);
    setQrDataUrl("");
    setError("");
    setNotice("");
  }

  function selectChannel(next: RechargeChannel) {
    if (next !== channel) resetPayment();
    setChannel(next);
  }

  return <section className="personal-lawyer-panel personal-balance-panel" aria-label="余额账户">
    <div className="personal-balance-summary">
      <article className="is-total"><span>当前可用余额</span><strong>{money(wallet.total_balance)}</strong><small>充值余额与奖励余额可合并用于平台服务</small></article>
      <article><span>充值余额</span><strong>{money(wallet.recharge_balance)}</strong><small>自主充值及平台人工充值</small></article>
      <article><span>奖励余额</span><strong>{money(wallet.reward_balance)}</strong><small>邀请奖励，仅限平台内使用</small></article>
    </div>
    <div className={`personal-recharge-card${recharge ? " has-code" : ""}`}>
      <div className="personal-recharge-form">
        <h2>在线充值</h2>
        <label>充值金额（元）<input disabled={busy} value={amount} inputMode="decimal" onChange={(event) => { setAmount(event.target.value); resetPayment(); }} /></label>
        <div className="personal-recharge-channels" role="group" aria-label="支付方式">
          <button type="button" disabled={busy || !availableChannels.includes("WECHAT")} className={channel === "WECHAT" ? "is-active" : ""} aria-pressed={channel === "WECHAT"} onClick={() => selectChannel("WECHAT")}><span>微信支付</span>{!availableChannels.includes("WECHAT") && <small>暂未开通</small>}</button>
          <button type="button" disabled={busy || !availableChannels.includes("ALIPAY")} className={channel === "ALIPAY" ? "is-active" : ""} aria-pressed={channel === "ALIPAY"} onClick={() => selectChannel("ALIPAY")}><span>支付宝支付</span>{!availableChannels.includes("ALIPAY") && <small>暂未开通</small>}</button>
        </div>
        <button className="personal-recharge-submit" type="button" disabled={busy || availableChannels.length === 0 || !channel || !isRechargeAmountValid(amount)} onClick={() => void submit()}>{busy ? "正在生成…" : "生成付款二维码"}</button>
        {error && <p className="form-error">{error}</p>}{notice && <p className="form-success">{notice}</p>}
      </div>
      {recharge && <div className="personal-recharge-code">{qrDataUrl ? <img src={qrDataUrl} alt={rechargeQrAlt(recharge.channel)} /> : <div className="personal-qr-loading">正在生成付款二维码…</div>}<strong>{money(Number(recharge.amount))}</strong><b>{recharge.channel === "WECHAT" ? "微信支付" : "支付宝支付"}</b><small>{rechargeStatusCopy(recharge.status)}</small></div>}
    </div>
    <section className="personal-recharge-history" aria-labelledby="personal-recharge-history-title">
      <div className="personal-recharge-history-title"><h2 id="personal-recharge-history-title">充值流水</h2></div>
      <div className="personal-wallet-ledger"><table><thead><tr><th>到账时间</th><th>付款方式</th><th>账户</th><th>充值金额</th></tr></thead><tbody>{wallet.recharges.length === 0 ? <tr><td colSpan={4}>暂无充值流水</td></tr> : wallet.recharges.map((item) => <tr key={item.id}><td>{new Date(item.paid_at).toLocaleString("zh-CN")}</td><td>{item.channel === "WECHAT" ? "微信支付" : "支付宝支付"}</td><td>充值余额</td><td className="is-credit">{money(item.amount)}</td></tr>)}</tbody></table></div>
      <nav className="personal-recharge-pagination" aria-label="充值流水分页">
        <span>共 {wallet.recharges_total} 条，每页 {RECHARGES_PER_PAGE} 条</span>
        <div>
          <button type="button" disabled={wallet.recharge_page === 1} onClick={() => void onRechargePageChange?.(wallet.recharge_page - 1)}>上一页</button>
          {paginationPageNumbers(wallet.recharge_page, wallet.recharge_page_count).map((pageNumber) => <button type="button" key={pageNumber} className={pageNumber === wallet.recharge_page ? "is-current" : ""} aria-current={pageNumber === wallet.recharge_page ? "page" : undefined} onClick={() => void onRechargePageChange?.(pageNumber)}>{pageNumber}</button>)}
          <button type="button" disabled={wallet.recharge_page === wallet.recharge_page_count} onClick={() => void onRechargePageChange?.(wallet.recharge_page + 1)}>下一页</button>
        </div>
      </nav>
    </section>
  </section>;
}
